import {useContext, useEffect} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useRosterStore} from '@/store/roster_store';
import {SearchParam} from '@/helpers/search_params';
import RosterHelpers from '@/helpers/roster_helpers';

/**
 * Resolves the roster a care journey page is operating on, and publishes it as the store's
 * `taskRoster` so the journey subtree can act on it.
 *
 * Task pages are cross-roster: the list shows journeys from every group the user can access and the
 * group picker is hidden. Journey links carry the journey's own group in the `r` search param, and
 * the API rejects any write whose `roster_id` does not match the journey's group. Every mutation
 * rendered under a task detail page reads the roster via `useActiveRoster`, which prefers
 * `taskRoster`, so setting `taskRoster` here makes both reads and writes target the journey's group
 * without disturbing the facility picker's `currentRoster`.
 *
 * `taskRoster` is cleared when the journey page unmounts, so the rest of the app falls back to
 * `currentRoster`. When `r` is absent we keep the previous behaviour: leave an already selected
 * task roster alone, and otherwise fall back to the first roster available to the user.
 */
export default function useJourneyRoster() {
  const [searchParams] = useSearchParams();
  const {carecoApi} = useContext(CarecoApiContext);
  const {taskRoster, setTaskRoster} = useRosterStore();

  const rosterIdParam = searchParams.get(SearchParam.ROSTER_ID);
  const paramRosterId = rosterIdParam ? Number(rosterIdParam) : undefined;

  const needsSync = paramRosterId ? taskRoster?.id !== paramRosterId : !taskRoster;

  const {data: rosters} = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi && needsSync,
  });

  useEffect(() => {
    if (!rosters || !needsSync) return;

    const roster = paramRosterId
      ? RosterHelpers.getRosterById(rosters, paramRosterId)
      : rosters.data?.data?.[0];

    if (roster) setTaskRoster(roster);
  }, [rosters, paramRosterId, needsSync, setTaskRoster]);

  // The task roster only exists while a journey page is mounted. Clearing it on unmount lets the
  // rest of the app fall back to the facility picker's `currentRoster` (see useActiveRoster).
  useEffect(() => () => setTaskRoster(null), [setTaskRoster]);

  return {
    roster: taskRoster,
    // Prefer the journey's group from the URL; the backend guards proper permissions.
    rosterId: paramRosterId ?? taskRoster?.id,
    // True while the store still points at a different group than the journey's.
    isRosterSyncing: needsSync,
  };
}
